import React, { useState } from 'react';
import { useFieldHelper, DateField, FieldProps } from 'framework/ui/field';
import { Box, Typography, Divider, Stack } from '@mui/material';
import { MapedPoliciesDataType, PolicyStageBadgeField } from 'domain/shared/compliance';
import { Button } from 'framework/ui/mui-builder';
import Popover from '@mui/material/Popover';

const PoliciesSigningField = ({ expanded }: FieldProps) => {
  const { getValue } = useFieldHelper<MapedPoliciesDataType[]>({ source: 'policies' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentPolicy, setCurrentPolicy] = useState<string | null>(null);

  const policies = getValue();
  const data = expanded ? policies : policies.slice(0, 2);

  const handleClick = (event: React.MouseEvent<HTMLElement>, policyPath: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentPolicy(policyPath);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentPolicy(null);
  };

  const open = Boolean(anchorEl) && currentPolicy !== null;

  return (
    <Box>
      {data.map(({ policyPath, policyType, policyVersion, consentTimestamp, previousVersions = [] }) => {
        const hasPreviousVersions = previousVersions.length > 0;
        const isCurrentOpen = open && currentPolicy === policyPath;

        const time =
          consentTimestamp ||
          (previousVersions?.length ? previousVersions.find(item => item.consentTimestamp)?.consentTimestamp : null);
        const version = consentTimestamp
          ? policyVersion
          : previousVersions?.find(item => item.consentTimestamp)?.policyVersion;

        return (
          <Box key={policyPath} sx={{ position: 'relative' }} mb="2px">
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} sx={{ minHeight: 24 }}>
              <Box display="flex" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <Typography mr={1}>v.{version || policyVersion}</Typography>
                <DateField value={time} />
              </Box>

              <Box sx={{ width: '50px', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                {hasPreviousVersions && (
                  <Button
                    link
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLElement>) => handleClick(e, policyPath)}
                    sx={{
                      padding: 0,
                      fontSize: '10px',
                      minWidth: '50px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                    aria-label="show previous versions"
                  >
                    History
                  </Button>
                )}
              </Box>
            </Box>

            {hasPreviousVersions && (
              <Popover
                open={isCurrentOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                onClick={e => e.stopPropagation()}
                anchorOrigin={{
                  vertical: 25,
                  horizontal: -250,
                }}
                slotProps={{
                  paper: {
                    sx: {
                      p: 2,
                      minWidth: 300,
                      maxHeight: 235,
                      overflow: 'auto',
                      boxShadow: '0px 4px 20px var(--mui-palette-common-shadowMedium)',
                      borderRadius: 1,
                    },
                  },
                }}
              >
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" color="primary">
                      {policyType}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Current version: v.{policyVersion}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />

                  <Typography variant="body2" color="textSecondary">
                    Previous versions
                  </Typography>

                  {previousVersions.map(versionItem => (
                    <Box key={versionItem.policyPath}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <PolicyStageBadgeField value={versionItem.isPolicyAccepted} />

                        <Box key={policyPath} display="flex" alignItems="center" mb="3.5px">
                          <Typography mr={1}>v.{versionItem.policyVersion}</Typography>
                          <DateField value={time} />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Popover>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default PoliciesSigningField;
